/* World View — container that owns shared fetch/filter/inspector state and
   switches between a 3D globe and a 2D flat-map canvas based on `mapMode`.
   Layout inspired by worldmonitor (AGPL-3.0); implementation original. */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react"
import { api } from "./api"
import { apiHeaders } from "./api-base"
import { AssetInspector } from "./AssetInspector"
import { type CountryFeature, loadCountries } from "./countries"
import { GlobeCanvas } from "./GlobeCanvas"
import { altitudeColor } from "./globe-colors"
import { LayerPanel } from "./LayerPanel"
import { defaultLayerState, LAYERS } from "./layers"
import { useApp } from "./store"
import type { GlobeArc, GlobePayload, GlobePoint } from "./types"
import { useAssetTrails } from "./useAssetTrails"
import { Layers, X } from "lucide-react"

// The MapLibre + deck.gl bundle is heavy; lazy-load it so the 3D-first default
// keeps the initial TTI unchanged. The chunk only arrives when a user clicks "2D".
const FlatMapCanvas = lazy(() =>
  import("./FlatMapCanvas").then((m) => ({ default: m.FlatMapCanvas })),
)

const REFRESH_MS = 30_000

const KIND_FOR_LAYER: Record<string, GlobePoint["kind"] | null> = Object.fromEntries(
  LAYERS.map((l) => [l.id, l.kind === "future" ? null : (l.kind as GlobePoint["kind"] | null)]),
)

export function GlobeView({ embedded = false }: { embedded?: boolean } = {}) {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [payload, setPayload] = useState<GlobePayload | null>(null)
  const [enabled, setEnabled] = useState<Record<string, boolean>>(defaultLayerState())
  const [severityMin, setSeverityMin] = useState<0 | 1 | 2 | 3>(0)
  const [ownedOnly, setOwnedOnly] = useState(false)
  const [selected, setSelected] = useState<GlobePoint | null>(null)
  const [size, setSize] = useState({ w: 800, h: 600 })
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  const [adopting, setAdopting] = useState(false)
  const [adoptedIds, setAdoptedIds] = useState<Set<string>>(new Set())
  const [now, setNow] = useState(() => Date.now())
  const [countries, setCountries] = useState<CountryFeature[]>([])
  const [hoveredCountry, setHoveredCountry] = useState<CountryFeature | null>(null)
  const [layersOpen, setLayersOpen] = useState(false)

  const mapMode = useApp((s) => s.mapMode)
  const setMapMode = useApp((s) => s.setMapMode)

  const activeKinds = useMemo(() => {
    const kinds = new Set<string>()
    for (const l of LAYERS) {
      const k = KIND_FOR_LAYER[l.id]
      if (enabled[l.id] && k) kinds.add(k)
    }
    return kinds
  }, [enabled])

  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      try {
        const layers = Array.from(activeKinds).join(",")
        if (!layers) {
          if (!cancelled) {
            setPayload({ points: [], counts: {}, colors: {} as any, as_of: Math.floor(Date.now() / 1000) })
            setLoading(false)
          }
          return
        }
        const url = `${api.baseUrl}/api/realtime/globe?layers=${layers}`
        const res = await fetch(url, { credentials: "include", headers: apiHeaders() })
        if (!res.ok) throw new Error(`globe fetch ${res.status}`)
        const data = (await res.json()) as GlobePayload
        if (!cancelled) {
          setPayload(data)
          setNow(Date.now())
          setLoading(false)
          setFetchError(null)
        }
      } catch (err) {
        console.warn("globe fetch error", err)
        if (!cancelled) {
          setLoading(false)
          setFetchError(err instanceof Error ? err.message : String(err))
        }
      }
    }
    fetchData()
    const h = setInterval(fetchData, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(h)
    }
  }, [activeKinds, retryNonce])

  // Lazy-load world-countries geometry the first time the `countries`
  // layer is toggled on. The loader itself caches at module scope, so
  // toggling off+on again is a no-op network-wise.
  useEffect(() => {
    if (!enabled["countries"] || countries.length > 0) return
    let cancelled = false
    loadCountries()
      .then((features) => {
        if (!cancelled) setCountries(features)
      })
      .catch((err) => {
        console.warn("countries load error", err)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, countries.length])

  useEffect(() => {
    const handle = () => {
      const el = containerRef.current
      if (el) setSize({ w: el.clientWidth, h: el.clientHeight })
    }
    handle()
    const observer = new ResizeObserver(handle)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const filteredPoints = useMemo(() => {
    if (!payload) return [] as GlobePoint[]
    const sevRank: Record<string, number> = {
      info: 0, low: 0, medium: 1, warn: 1, high: 2, critical: 3,
    }
    return payload.points.filter((p) => {
      const layer = LAYERS.find((l) => KIND_FOR_LAYER[l.id] === p.kind)
      if (!layer || !enabled[layer.id]) return false
      if (ownedOnly && !p.owned && !adoptedIds.has(p.id)) return false
      if (p.kind === "incident") {
        const rank = sevRank[p.severity ?? "info"] ?? 0
        if (rank < severityMin) return false
      }
      return true
    })
  }, [payload, enabled, severityMin, ownedOnly, adoptedIds])

  const decorated = useMemo(
    () =>
      filteredPoints.map((p) => {
        const isOwned = p.owned || adoptedIds.has(p.id)
        const color = isOwned
          ? "#facc15"
          : p.kind === "flight"
            ? altitudeColor(p.altitude_m)
            : p.color
        return { ...p, color, owned: isOwned }
      }),
    [filteredPoints, adoptedIds],
  )

  const polygonsData = useMemo(
    () => (enabled["countries"] ? countries : []),
    [enabled, countries],
  )

  // Trails accumulate client-side across poll cycles, even while filters
  // churn `decorated` — `useAssetTrails` owns the sliding store internally.
  const trails = useAssetTrails(decorated)

  const layerCounts: Record<string, number> = useMemo(() => {
    const out: Record<string, number> = {}
    for (const l of LAYERS) out[l.id] = 0
    for (const p of decorated) {
      for (const l of LAYERS) {
        if (KIND_FOR_LAYER[l.id] === p.kind) out[l.id]!++
      }
    }
    // Client-only layers: surface polygon count in the sidebar when active.
    out["countries"] = polygonsData.length
    return out
  }, [decorated, polygonsData])

  const arcs = useMemo(() => {
    if (!enabled["fleet-arcs"]) return [] as GlobeArc[]
    const all = payload?.arcs ?? []
    return all.filter((a) => (ownedOnly ? a.owned : true))
  }, [payload?.arcs, enabled, ownedOnly])

  const adopt = async (p: GlobePoint) => {
    setAdopting(true)
    try {
      const extId = p.id.split(":").pop() ?? p.id
      const resp = await fetch(`${api.baseUrl}/api/realtime/adopt`, {
        method: "POST",
        credentials: "include",
        headers: apiHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          kind: p.kind,
          external_id: extId,
          label: p.label,
          lat: p.lat,
          lng: p.lng,
          heading: p.heading,
          speed_kph: p.velocity_kt ? p.velocity_kt * 1.852 : null,
          meta: { mmsi: p.mmsi, country: p.country },
        }),
      })
      if (resp.ok) setAdoptedIds((prev) => new Set(prev).add(p.id))
    } finally {
      setAdopting(false)
    }
  }

  const updatedAgoSec = Math.max(0, Math.floor((Date.now() - now) / 1000))

  return (
    <div className="relative h-full w-full flex flex-col overflow-hidden">
      {!embedded && (
        <div className="header-bar">
          <div className="header-logo">SUPPLYLENS · WORLD VIEW</div>
          <div className="text-[11px] text-[color:var(--text-muted)] flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span
                className={`status-dot ${loading ? "connecting" : fetchError ? "down" : "live"}`}
              />
              <span className="uppercase tracking-wider">
                {loading ? "loading" : fetchError ? "offline" : `live · ${updatedAgoSec}s ago`}
              </span>
            </span>
            <span className="text-[color:var(--text-dim)]">·</span>
            <span className="tabular-nums">{decorated.length.toLocaleString()} visible</span>
          </div>
          <div className="flex-1" />
          <div className="text-[10px] text-[color:var(--text-dim)]">
            flights OpenSky · ships synthetic · incidents disruption-radar
          </div>
        </div>
      )}

      <div className="flex-1 flex relative min-h-0">
        <div ref={containerRef} className="map-canvas-wrap flex-1 min-w-0 relative bg-[color:var(--map-bg)]">
          {mapMode === "3d" ? (
            <GlobeCanvas
              width={size.w}
              height={size.h}
              points={decorated}
              arcs={arcs}
              countries={countries}
              showCountries={!!enabled["countries"]}
              hoveredCountry={hoveredCountry}
              onHoverCountry={setHoveredCountry}
              onPointClick={(p) => setSelected(p)}
              trails={trails}
            />
          ) : (
            <Suspense fallback={<FlatMapLoading />}>
              <FlatMapCanvas
                points={decorated}
                arcs={arcs}
                showArcs={!!enabled["fleet-arcs"]}
                onPointClick={(p) => setSelected(p)}
                trails={trails}
              />
            </Suspense>
          )}

          {/* Non-blocking fetch-failure banner — the globe keeps rendering the
              last good payload (or empty space) while we retry. */}
          {fetchError && (
            <div className="absolute top-3 left-3 z-20 ui-panel--glass rounded border border-red-500/40 px-3 py-2 text-[11px] text-red-300 max-w-sm pointer-events-auto">
              <div className="font-bold uppercase tracking-wider">globe feed unreachable</div>
              <div className="mt-0.5 text-[10px] text-red-200/80 break-all">
                {api.baseUrl}/api/realtime/globe — {fetchError}
              </div>
              <button
                type="button"
                className="btn-pill mt-1.5"
                onClick={() => {
                  setLoading(true)
                  setRetryNonce((n) => n + 1)
                }}
              >
                retry now
              </button>
            </div>
          )}

          {/* Top-right: 3D / 2D mode toggle. Sits above the layer sidebar. */}
          <div className="map-controls absolute top-3 right-3 ui-panel--glass px-2 py-1.5 flex items-center gap-1 pointer-events-auto">
            <span className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] mr-1">
              view
            </span>
            <button
              type="button"
              aria-pressed={mapMode === "3d"}
              onClick={() => setMapMode("3d")}
              className="btn-pill"
              title="3D globe"
            >
              3D
            </button>
            <button
              type="button"
              aria-pressed={mapMode === "2d"}
              onClick={() => setMapMode("2d")}
              className="btn-pill"
              title="2D flat map"
            >
              2D
            </button>
            <button type="button" className="btn-pill map-layers-button" aria-label="Toggle map layers" aria-expanded={layersOpen} onClick={() => setLayersOpen(!layersOpen)}><Layers size={13} />Layers</button>
          </div>

          <div className="map-caption"><span className="map-caption-dot" /><span>GLOBAL COVERAGE<small>Flights · ships · disruption signals</small></span></div>

          <div className="map-legend absolute bottom-3 left-3 ui-panel--glass px-3 py-2 text-[11px] flex gap-5 items-center pointer-events-none">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#facc15" }} />
              <span>Owned fleet</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-10 h-1.5 rounded-sm" style={{
                background: "linear-gradient(90deg, #00d9ff, #32fa9e, #c8e63c, #ffb428, #ff6e23, #eb3237)"
              }} />
              <span>flight altitude</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#f59e0b" }} />
              <span>disruption · medium</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#ef4444" }} />
              <span>disruption · critical</span>
            </div>
          </div>
          <div className="map-source">Ships: simulated · Flights: OpenSky · Incidents: disruption feed</div>

          {selected && (
            <AssetInspector
              selected={selected}
              adopting={adopting}
              adoptedIds={adoptedIds}
              onClose={() => setSelected(null)}
              onAdopt={adopt}
            />
          )}
        </div>

        {layersOpen && <aside className="map-layer-sidebar bg-[color:var(--surface)] border-l border-[color:var(--border)]">
          <div className="map-layer-heading"><strong>Map layers</strong><button className="icon-button" aria-label="Close map layers" onClick={() => setLayersOpen(false)}><X size={16} /></button></div>
          <LayerPanel
            enabled={enabled}
            counts={layerCounts}
            onToggle={(id) =>
              setEnabled((prev) => ({ ...prev, [id]: !(prev[id] ?? false) }))
            }
            severityMin={severityMin}
            onSeverityMinChange={setSeverityMin}
            ownedOnly={ownedOnly}
            onOwnedOnlyChange={setOwnedOnly}
          />
        </aside>}
      </div>
    </div>
  )
}

function FlatMapLoading() {
  return (
    <div className="absolute inset-0 flex items-center justify-center text-[color:var(--text-dim)] text-xs uppercase tracking-widest">
      loading map…
    </div>
  )
}
